<template>
  <div class="jewel-simulator">
    <h1>寶玉模擬器</h1>
    
    <div class="result-section">
      <h2>能力值：+ {{ finalValue }}</h2>
    </div>

    <div class="simulator-container">
      <div class="main-attributes">
        <div class="section-header">
          <h2>主屬性</h2>
          <div class="total-attribute">
            總和：<span class="value">{{ totalMainValue }}</span>
          </div>
        </div>
        <div class="attribute-list">
          <div v-for="level in 10" :key="level" class="attribute-item">
            <div class="attribute-row">
              <label>等級 {{ level }}：</label>
              <select v-model="selectedMainValues[level]" @change="calculateFinalValue">
                <option value="0">請選擇數值</option>
                <option v-for="value in mainAttributeValues[level]" :key="value" :value="value">
                  {{ value }}
                </option>
              </select>
              <button 
                v-if="isLowestEmptyLevel(level)"
                @click="upgradeMainAttribute(level)" 
                class="upgrade-button"
              >
                升級
              </button>
            </div>
          </div>
        </div>
        <div class="main-attribute-footer">
          <div class="dragon-scale-cost">
            總和：<span class="value">{{ totalMainValue }}</span>
            累積消耗龍鱗：<span class="value">{{ totalDragonScaleCost }}</span>
          </div>
          <div class="upgrade-controls">
            <button 
              @click="resetMainAttributes"
              class="reroll-button"
              :disabled="!mainAttributesFull"
            >
              重洗
            </button>
            <button 
              @click="resetDragonScaleCost" 
              class="reset-dragon-scale-button"
            >
              重設龍鱗
            </button>
          </div>
        </div>
      </div>

      <div class="level-section">
        <div class="section-header">
          <h2>追加屬性</h2>
          <div class="total-attribute">
            當前等級：<span class="value">{{ currentLevel }}</span>
          </div>
        </div>
        <div class="level-controls">
          <div class="current-level">
            <label>當前等級：</label>
            <input 
              type="number" 
              v-model.number="currentLevel" 
              min="0" 
              max="10"
              @input="validateLevel"
            >
          </div>
          <div class="target-level">
            <label>目標等級：</label>
            <div class="target-level-control">
              <span class="target-level-display">{{ targetLevel }}</span>
              <div class="level-buttons">
                <button @click="decreaseTargetLevel" class="level-btn" :disabled="targetLevel <= 0">-</button>
                <button @click="increaseTargetLevel" class="level-btn" :disabled="targetLevel >= 10">+</button>
              </div>
            </div>
          </div>
        </div>
        <div class="level-info">
          <p>楓幣消耗：{{ mesoCost }}億</p>
          <p>成功機率：{{ successRate }}%</p>
          <p>下降機率：{{ decreaseRate }}%</p>
          <p>失敗機率：{{ failRate }}%</p>
        </div>
        <div class="total-cost-info">
          累積消耗楓幣：<span class="value">{{ totalMesoCost }}億</span>
        </div>
        <div class="button-group">
          <button @click="resetMesoSpent" class="setup-button">重設累積楓幣</button>
          <button @click="attemptUpgrade" :disabled="!canUpgrade" class="upgrade-multiple-button">升級</button>
        </div>
        <div class="upgrade-multiple-group">
          <button @click="upgradeMultiple(10)" :disabled="!canUpgrade" class="upgrade-multiple-button">升級十次</button>
          <div class="custom-upgrade">
            <input 
              type="number" 
              v-model.number="customUpgradeCount" 
              min="1" 
              placeholder="次數"
              class="custom-upgrade-input"
            >
            <button @click="upgradeMultiple(customUpgradeCount)" :disabled="!canUpgrade || !customUpgradeCount" class="upgrade-multiple-button">升級</button>
          </div>
        </div>
        <div class="reset-button-group">
          <button @click="resetMainAttributes" class="reset-button">重設主屬性</button>
          <button @click="resetAdditionalAttributes" class="reset-button">重設追加屬性</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { JewelCalculator } from '@/utils/JewelCalculator'

export default {
  name: 'JewelSimulator',
  data() {
    return {
      currentLevel: 0,
      targetLevel: 10,
      isSetupComplete: false,
      totalMesoSpent: 0,
      customUpgradeCount: 1,
      mainAttributeLevel: 1,
      totalDragonScaleCost: 0,
      selectedMainValues: {
        1: 0, 2: 0, 3: 0, 4: 0, 5: 0,
        6: 0, 7: 0, 8: 0, 9: 0, 10: 0
      }
    }
  },
  computed: {
    mainAttributeValues() {
      const values = {};
      for (let i = 1; i <= 10; i++) {
        values[i] = JewelCalculator.getMainAttributeValues(i);
      }
      return values;
    },
    mainAttributesFull() {
      return Object.values(this.selectedMainValues).every(v => Number(v) > 0);
    },
    totalMainValue() {
      return JewelCalculator.calculateTotalMainValue(this.selectedMainValues);
    },
    finalValue() {
      return JewelCalculator.calculateFinalValue(this.selectedMainValues, this.currentLevel);
    },
    additionalAttribute() {
      return JewelCalculator.getAdditionalAttribute(this.currentLevel);
    },
    mesoCost() {
      return JewelCalculator.getMesoCost(this.currentLevel);
    },
    successRate() {
      return JewelCalculator.getSuccessRate(this.currentLevel);
    },
    decreaseRate() {
      return JewelCalculator.getDecreaseRate(this.currentLevel);
    },
    failRate() {
      return JewelCalculator.getFailRate(this.currentLevel);
    },
    canUpgrade() {
      return this.currentLevel < 10 && this.currentLevel < this.targetLevel;
    },
    totalMesoCost() {
      return this.totalMesoSpent;
    }
  },
  mounted() {
    this.loadMainAttributesFromLocalStorage();
  },
  methods: {
    calculateFinalValue() {
      Object.keys(this.selectedMainValues).forEach(key => {
        const value = this.selectedMainValues[key]
        if (value === null || value === undefined || isNaN(value)) {
          this.selectedMainValues[key] = 0
        }
      })

      // 儲存主屬性到 localStorage
      localStorage.setItem('selectedMainValues', JSON.stringify(this.selectedMainValues));
    },
    attemptUpgrade() {
      if (!this.canUpgrade) return

      const { newLevel, mesoCost } = JewelCalculator.attemptUpgrade(this.currentLevel)
      this.totalMesoSpent += mesoCost
      this.currentLevel = newLevel

      // 儲存主屬性到 localStorage
      localStorage.setItem('selectedMainValues', JSON.stringify(this.selectedMainValues));
    },
    validateLevel() {
      this.currentLevel = JewelCalculator.validateLevel(this.currentLevel)
    },
    validateTargetLevel() {
      this.targetLevel = JewelCalculator.validateLevel(this.targetLevel)
    },
    increaseTargetLevel() {
      if (this.targetLevel < 10) {
        this.targetLevel++
      }
    },
    decreaseTargetLevel() {
      if (this.targetLevel > 0) {
        this.targetLevel--
      }
    },
    resetMesoSpent() {
      this.totalMesoSpent = 0
      this.currentLevel = 0
    },
    resetMainAttributes() {
      this.selectedMainValues = JewelCalculator.resetMainValues(this.selectedMainValues);
      localStorage.setItem('selectedMainValues', JSON.stringify(this.selectedMainValues));
    },
    resetAdditionalAttributes() {
      this.currentLevel = 0
      this.totalMesoSpent = 0
      this.isSetupComplete = false
    },
    upgradeMultiple(count) {
      if (!this.canUpgrade || !count) return
      
      for (let i = 0; i < count; i++) {
        if (!this.canUpgrade) break
        this.attemptUpgrade()
      }
    },
    loadMainAttributesFromLocalStorage() {
      const storedMainValues = JSON.parse(localStorage.getItem('selectedMainValues'));
      if (storedMainValues) {
        this.selectedMainValues = { ...this.selectedMainValues, ...storedMainValues };
      }
      const storedDragonScaleCost = localStorage.getItem('totalDragonScaleCost');
      if (storedDragonScaleCost) {
        this.totalDragonScaleCost = parseInt(storedDragonScaleCost);
      }
    },
    isLowestEmptyLevel(level) {
      return JewelCalculator.isLowestEmptyLevel(level, this.selectedMainValues);
    },
    upgradeMainAttribute(level) {
      if (level > 10) return;
      
      this.selectedMainValues[level] = JewelCalculator.getRandomMainValue(level);
      this.totalDragonScaleCost += JewelCalculator.getUpgradeDragonScaleCost();
      
      localStorage.setItem('selectedMainValues', JSON.stringify(this.selectedMainValues));
      localStorage.setItem('totalDragonScaleCost', this.totalDragonScaleCost.toString());
    },
    resetDragonScaleCost() {
      this.totalDragonScaleCost = 0;
      this.selectedMainValues = JewelCalculator.resetMainValues(this.selectedMainValues);
      localStorage.setItem('totalDragonScaleCost', '0');
      localStorage.setItem('selectedMainValues', JSON.stringify(this.selectedMainValues));
    }
  }
}
</script>

<style scoped>
.jewel-simulator {
  max-width: 800px;
  margin: 0 auto;
}

.jewel-simulator h1 {
  text-align: left;
  margin: 2rem 0;
}

.result-section {
  text-align: center;
  margin: 2rem 0;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.simulator-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;
}

.main-attributes, .level-section {
  padding: 1.5rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.main-attributes > *, .level-section > * {
  margin-top: 0;
}

.attribute-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.attribute-item {
  background-color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.attribute-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.attribute-row label {
  min-width: 70px;
  color: var(--text-color);
  font-weight: 500;
  margin: 0;
}

.attribute-row select {
  flex: 1;
  max-width: 120px;
  padding: 0.4rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  background-color: white;
}

.attribute-row select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
}

.attribute-row .upgrade-button {
  padding: 0.4rem 1rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-left: auto;
}

.attribute-row .upgrade-button:hover {
  background-color: #357abd;
}

.attribute-row .upgrade-button:disabled {
  background-color: #e0e0e0;
  color: #9e9e9e;
  cursor: not-allowed;
}

.level-controls {
  background-color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.current-level, .target-level {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.current-level label, .target-level label {
  min-width: 80px;
  font-weight: 500;
}

.current-level input {
  width: 60px;
  padding: 0.4rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
}

.current-level input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
}

.target-level-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.target-level-display {
  width: 60px;
  padding: 0.4rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
  background-color: #f8f9fa;
  color: var(--text-color);
  font-weight: 500;
}

.level-buttons {
  display: flex;
  gap: 0.25rem;
}

.level-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: bold;
  transition: all var(--transition-speed) ease;
}

.level-btn:hover {
  background-color: var(--nav-hover);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.level-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.level-info, .probability-info,.total-cost-info {
  margin: 0.5rem 0;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.total-cost-info {
  font-size: 1.1rem;
  text-align: center;
  color: var(--text-color);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.total-cost-info .value {
  font-weight: 700;
  color: var(--primary-color);
}

.level-info p, .probability-info p {
  margin: 0.5rem 0;
  color: var(--text-color);
}

.setup-button {
  flex: 1;
  padding: 0.8rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.setup-button:hover {
  background-color: #218838;
}

.setup-button:disabled {
  background-color: #e0e0e0;
  color: #9e9e9e;
  cursor: not-allowed;
}

.reroll-button, .upgrade-button, .reset-dragon-scale-button {
  padding: 0.4rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.reroll-button {
  background-color: #ffc107;
  color: #000;
}

.reroll-button:hover {
  background-color: #ffb300;
}

.reroll-button:disabled {
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
  cursor: not-allowed;
}

.reset-dragon-scale-button {
  background-color: #dc3545;
  color: white;
}

.reset-dragon-scale-button:hover {
  background-color: #c82333;
}

h1, h2, h3 {
  color: var(--text-color);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  height: 40px;
  margin-top: 0;
  padding-top: 0;
}

.section-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--text-color);
}

.total-attribute {
  font-size: 1.1rem;
  color: var(--text-color);
  display: flex;
  align-items: center;
  height: 100%;
  margin-left: 2rem;
}

.total-attribute .value {
  font-weight: 700;
  color: var(--primary-color);
  margin-left: 0.25rem;
}

.upgrade-multiple-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
}

.custom-upgrade {
  display: flex;
  gap: 0.5rem;
}

.custom-upgrade-input {
  flex: 1;
  padding: 0.4rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
}

.custom-upgrade-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
}

.upgrade-multiple-button {
  flex: 1;
  padding: 0.8rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.upgrade-multiple-button:hover {
  background-color: #357abd;
}

.upgrade-multiple-button:disabled {
  background-color: #e0e0e0;
  color: #9e9e9e;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .simulator-container {
    grid-template-columns: 1fr;
  }
  .attribute-list {
    grid-template-columns: 1fr;
  }
}

.main-title {
  text-align: center;
  font-size: 2.5rem;
  font-weight: bold;
  color: var(--primary-color);
  margin-bottom: 2rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  border-bottom: 3px solid var(--primary-color);
}

.button-group, .upgrade-multiple-group, .reset-button-group {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.button-group button, .reset-button {
  flex: 1;
  padding: 0.8rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  border: none;
  border-radius: 4px;
}

.reset-button {
  background-color: #dc3545;
  color: white;
}

.reset-button:hover {
  background-color: #c82333;
}

.reset-button:disabled {
  background-color: #e0e0e0;
  color: #9e9e9e;
  cursor: not-allowed;
}

.main-attribute-footer {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dragon-scale-cost {
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: var(--text-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dragon-scale-cost .value {
  font-weight: 700;
  color: var(--primary-color);
}

.upgrade-controls {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: center;
}

.upgrade-controls button {
  flex: 1;
  padding: 0.8rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;
  border: none;
  border-radius: 4px;
}

.level-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
</style> 
